import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AvaBotType } from './types';
import { AvaChatbotMessage } from './useAvaConversationMessages';

const isJsonString = (str: string): boolean => {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
};

export interface AskAvaWithStreamParams {
  bot_type: AvaBotType;
  conversation_id: number;
  question?: string;
  stream?: boolean;
  onStreamStart?: () => void;
  onStreamLine?: (chunk: string) => void;
  onStreamEnd?: (response: AvaChatbotMessage) => Promise<void>;
  onThinking?: (content: string, icon?: string) => void;
  onThinkingEnd?: () => void;
  onThinkingStart?: (content: string, icon?: string) => void;
  shouldExecuteCallbacks?: () => boolean;
}

interface AvaStreamLine {
  event_type: 'full_response' | 'response' | 'thinking' | 'thinking_start' | 'thinking_end';
  chunk?: string;
  content?: string;
  full_response?: string;
  icon?: string;
}

let aiqAssistChatController = new AbortController();

export const cancelAvaStream = () => {
  if (aiqAssistChatController) {
    aiqAssistChatController.abort('Request cancelled by user');
    aiqAssistChatController = new AbortController(); // Reset the controller for future requests.
  }
};

export const useAskAvaWithStream = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bot_type,
      conversation_id,
      question,
      onStreamStart,
      onStreamLine,
      onStreamEnd,
      onThinking,
      onThinkingEnd,
      onThinkingStart,
      shouldExecuteCallbacks
    }: AskAvaWithStreamParams) => {
      try {
        const response = await fetch(`aiq-assist/${bot_type}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/stream+json'
          },
          body: JSON.stringify({
            question: { conversation_id, question },
            stream: true
          }),
          signal: aiqAssistChatController.signal
        });

        if (!response.body) {
          throw new Error('Response does not contain a stream.');
        }

        if (shouldExecuteCallbacks?.() !== false) {
          onStreamStart?.();
        }

        // Read the response stream.
        const reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let result = '';
        let done = false;

        while (!done) {
          const { done: chunkDone, value }: ReadableStreamReadResult<Uint8Array> = await reader.read();
          done = chunkDone;

          // Decode the received chunk.
          result += decoder.decode(value, { stream: true });

          // Split the result by new lines (each line is a separate chunk).
          const stream = result.split('\n');

          // Keep the last part for further processing (it might be incomplete).
          result = stream.pop() || '';

          for (const streamLine of stream) {
            if (isJsonString(streamLine)) {
              try {
                const currentStreamLine: AvaStreamLine = JSON.parse(streamLine);
                const { event_type, chunk, content, full_response, icon } = currentStreamLine;

                switch (event_type) {
                  case 'thinking':
                    if (content) {
                      onThinking?.(content, icon);
                    }
                    break;
                  case 'thinking_start':
                    if (content) {
                      onThinkingStart?.(content, icon);
                    }
                    break;
                  case 'thinking_end':
                    onThinkingEnd?.();

                    break;
                  case 'response':
                    if (chunk && shouldExecuteCallbacks?.() !== false) {
                      onStreamLine?.(chunk);
                    }
                    break;
                  case 'full_response':
                    if (full_response && shouldExecuteCallbacks?.() !== false) {
                      const fullStreamResponse: AvaChatbotMessage = JSON.parse(full_response);
                      onStreamEnd?.(fullStreamResponse);
                    }
                    break;
                  default:
                    console.warn(`Unknown AVA streaming event type: ${event_type}`);
                }
              } catch (error) {
                console.error('Error parsing JSON:', error);
              }
            } else {
              console.warn('Invalid JSON string:', streamLine);
            }
          }
        }

        return response;
      } finally {
        aiqAssistChatController = new AbortController();
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['aiq-assist/messages'] })
  });
};
